import { useEffect, useRef, useState, type RefObject } from "react";
import type { MapMouseEvent, MapRef } from "react-map-gl/mapbox";

import { ROTATE_CURSOR } from "components/PlotMap/utils/meshPointerUtils";
import type { BoundaryPoint } from "context/PlotMap/geo";
import {
  findGridHandleAtScreenPoint,
  gridPointerAngleDegrees,
  isPointInGrid,
  moveGridTransform,
  resizeGridTransformFromCorner,
  rotateGridTransformByPointerDelta,
  type GridHandleHit,
  type PlotGrid,
} from "context/PlotMap/gridUtils";

export type GridTransformDragTarget =
  | { cornerIndex: number; type: "grid-corner" }
  | {
      offsetLatitude: number;
      offsetLongitude: number;
      type: "grid-move";
    }
  | {
      pointerAngleDegrees: number;
      rotationDegrees: number;
      type: "grid-rotation";
    };

interface UseGridTransformInteractionParams {
  beginGridHistoryGesture: () => void;
  canManipulateGridTransform: boolean;
  endGridHistoryGesture: () => void;
  grid: PlotGrid | null;
  isRotateModifierHeld: (event: MapMouseEvent) => boolean;
  mapRef: RefObject<MapRef | null>;
  updateGridTransform: (transform: PlotGrid["transform"]) => void;
}

interface UseGridTransformInteractionResult {
  handleMouseDown: (event: MapMouseEvent) => boolean;
  handleMouseMove: (event: MapMouseEvent) => boolean;
  handleMouseUp: () => void;
  isDragging: boolean;
  resolveHoverCursor: (
    event: MapMouseEvent,
    rotateModifierHeld?: boolean,
  ) => string | undefined;
}

const useGridTransformInteraction = ({
  beginGridHistoryGesture,
  canManipulateGridTransform,
  endGridHistoryGesture,
  grid,
  isRotateModifierHeld,
  mapRef,
  updateGridTransform,
}: UseGridTransformInteractionParams): UseGridTransformInteractionResult => {
  // Ref + state for the same drag target serve different paths:
  // - Ref: pointer handlers read/write synchronously (including mid-drag field
  //   updates) without waiting on a render or closing over a stale target.
  // - State: re-render for isDragging and effects/UI that depend on drag status
  //   (e.g. window mouseup, overlays).
  const draggingTargetRef = useRef<GridTransformDragTarget | null>(null);
  const [draggingTarget, setDraggingTarget] =
    useState<GridTransformDragTarget | null>(null);

  const beginDrag = (target: GridTransformDragTarget, event: MapMouseEvent) => {
    event.originalEvent.preventDefault();
    beginGridHistoryGesture();
    draggingTargetRef.current = target;
    setDraggingTarget(target);
    mapRef.current?.getMap().dragPan.disable();
  };

  const endPointerInteraction = () => {
    if (draggingTargetRef.current == null) {
      return;
    }

    const { transform } = grid ?? {};
    console.info(
      "[useGridTransformInteraction] end gesture",
      JSON.stringify({
        columns: grid?.columns ?? null,
        plotLengthMeters: grid?.plotLengthMeters ?? null,
        plotWidthMeters: grid?.plotWidthMeters ?? null,
        rotationDegrees: transform?.rotationDegrees ?? null,
        rows: grid?.rows ?? null,
        type: draggingTargetRef.current.type,
      }),
    );

    draggingTargetRef.current = null;
    setDraggingTarget(null);
    mapRef.current?.getMap().dragPan.enable();
    endGridHistoryGesture();
  };
  // Prefer a ref over useCallback in the window mouseup effect deps.
  // Example: mousedown starts a drag → effect attaches window mouseup; mousemove
  // re-renders and recreates endPointerInteraction. With useCallback in the effect
  // deps, each identity change would remove/re-add the listener mid-drag. The ref
  // keeps one subscription for the drag (deps: draggingTarget) while mouseup still
  // calls the latest endPointerInteraction.
  const endPointerInteractionRef = useRef(endPointerInteraction);
  endPointerInteractionRef.current = endPointerInteraction;

  useEffect(() => {
    if (draggingTarget == null) {
      return;
    }

    const handleWindowMouseUp = () => {
      endPointerInteractionRef.current();
    };

    window.addEventListener("mouseup", handleWindowMouseUp);
    return () => {
      window.removeEventListener("mouseup", handleWindowMouseUp);
    };
  }, [draggingTarget]);

  const getGridHandleAtScreenPoint = (
    screenPoint: MapMouseEvent["point"],
  ): GridHandleHit | null => {
    if (mapRef.current == null || grid == null || grid.isCommitted) {
      return null;
    }

    return findGridHandleAtScreenPoint(
      mapRef.current,
      grid.transform,
      screenPoint,
    );
  };

  const resolveHoverCursor = (
    event: MapMouseEvent,
    rotateModifierHeld?: boolean,
  ): string | undefined => {
    if (
      !canManipulateGridTransform ||
      grid == null ||
      draggingTargetRef.current != null
    ) {
      return undefined;
    }

    // Keyboard updates pass the live modifier. A stored mousemove still has
    // the modifiers from when it fired, so it cannot be trusted on its own.
    const rotateHeld = rotateModifierHeld ?? isRotateModifierHeld(event);
    const handleHit = getGridHandleAtScreenPoint(event.point);
    if (handleHit?.role === "corner") {
      if (rotateHeld) {
        return ROTATE_CURSOR;
      }
      const cornerIndex = handleHit.cornerIndex ?? 0;
      return cornerIndex === 0 || cornerIndex === 2
        ? "nesw-resize"
        : "nwse-resize";
    }

    const pointer: BoundaryPoint = {
      latitude: event.lngLat.lat,
      longitude: event.lngLat.lng,
    };
    if (isPointInGrid(pointer, grid.transform)) {
      return "move";
    }

    return undefined;
  };

  const handleMouseDown = (event: MapMouseEvent): boolean => {
    if (
      !canManipulateGridTransform ||
      grid == null ||
      event.originalEvent.button !== 0
    ) {
      return false;
    }

    const pointer: BoundaryPoint = {
      latitude: event.lngLat.lat,
      longitude: event.lngLat.lng,
    };

    const handleHit = getGridHandleAtScreenPoint(event.point);
    if (handleHit != null) {
      beginDrag(
        isRotateModifierHeld(event)
          ? {
              pointerAngleDegrees: gridPointerAngleDegrees(
                grid.transform,
                pointer,
              ),
              rotationDegrees: grid.transform.rotationDegrees,
              type: "grid-rotation",
            }
          : {
              cornerIndex: handleHit.cornerIndex ?? 0,
              type: "grid-corner",
            },
        event,
      );
      return true;
    }

    if (isPointInGrid(pointer, grid.transform)) {
      beginDrag(
        {
          offsetLatitude: grid.transform.centerLatitude - pointer.latitude,
          offsetLongitude: grid.transform.centerLongitude - pointer.longitude,
          type: "grid-move",
        },
        event,
      );
      return true;
    }

    return false;
  };

  const handleMouseMove = (event: MapMouseEvent): boolean => {
    const target = draggingTargetRef.current;
    if (target == null || grid == null || grid.isCommitted) {
      return false;
    }

    const pointer: BoundaryPoint = {
      latitude: event.lngLat.lat,
      longitude: event.lngLat.lng,
    };

    if (target.type === "grid-move") {
      updateGridTransform(
        moveGridTransform(grid.transform, {
          latitude: event.lngLat.lat + target.offsetLatitude,
          longitude: event.lngLat.lng + target.offsetLongitude,
        }),
      );
      return true;
    }

    if (target.type === "grid-corner") {
      updateGridTransform(
        resizeGridTransformFromCorner(
          grid.transform,
          pointer,
          target.cornerIndex,
        ),
      );
      return true;
    }

    const rotated = rotateGridTransformByPointerDelta(
      grid.transform,
      target.rotationDegrees,
      target.pointerAngleDegrees,
      pointer,
    );
    target.pointerAngleDegrees = rotated.pointerAngleDegrees;
    target.rotationDegrees = rotated.transform.rotationDegrees;
    updateGridTransform(rotated.transform);
    return true;
  };

  return {
    handleMouseDown,
    handleMouseMove,
    handleMouseUp: endPointerInteraction,
    isDragging: draggingTarget != null,
    resolveHoverCursor,
  };
};

export default useGridTransformInteraction;
